import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Banknote,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Globe,
  Lock,
  Pencil,
  Plus,
  RotateCcw,
  ShoppingCart,
  Store,
  Sunrise,
  Trash2,
  Undo2,
  Users,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AgingOpenOrdersAlert } from '@/components/admin/AgingOpenOrdersAlert'
import { UnreconciledOfflineItems } from '@/components/admin/UnreconciledOfflineItems'
import { PendingOfflineSyncAlert } from '@/components/admin/dayclose/PendingOfflineSyncAlert'
import { HeldCartsDayCloseNotice } from '@/components/admin/dayclose/HeldCartsDayCloseNotice'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCurrency } from '@/contexts/CurrencyContext'
import apiClient from '@/api/client'
import { useEnabledOrderTypes } from '@/hooks/useEnabledOrderTypes'
import { isMarketplaceSettledType } from '@/lib/orderTypeSettings'
import { slugifyProviderLabel } from '@/lib/marketplaceProviders'
import type {
  BusinessDay,
  CashDrawerMovement,
  CrossDayTender,
  CurrentDayStatus,
  ShiftClosing,
} from '@/types'
import { cn, formatDateHuman, formatTimeHuman, hourInBusinessTimezone } from '@/lib/utils'
import { toastHelpers } from '@/lib/toast-helpers'
import { OpenDayDialog } from './OpenDayDialog'
import { CashMovementDialog } from './CashMovementDialog'
import { StockCashOutItemsDialog } from '@/components/store/purchasing/StockCashOutItemsDialog'
import { BT } from '@/lib/inventoryFlow/boughtStrings'
import { CloseDayWizard } from './CloseDayWizard'
import { ShiftCloseWizard } from './ShiftCloseWizard'
import { ShiftReportButton } from './ShiftReportButton'
import { ReopenDayDialog } from './ReopenDayDialog'
import { ZReportButton } from './ZReportButton'
import { HourlySalesChart } from './HourlySalesChart'
import { HelpPopover } from './HelpPopover'
import { StartTheDayEmptyState } from './StartTheDayEmptyState'
import { ClosingStreakCard } from './ClosingStreakCard'
import {
  computeClosingStreak,
  dayStateLabel,
  expectedCash as computeExpectedCash,
  netCashAdjustments,
} from './dayCloseHelpers'
import { useHouseAccountsEnabled } from '@/hooks/useHouseAccountsEnabled'
/**
 * Re-exported so existing importers keep working; the implementation is the
 * shared mirror of backend paymentcfg.Slugify in lib/marketplaceProviders.
 */
export { slugifyProviderLabel } from '@/lib/marketplaceProviders'

export function DayClosePage() {
  const { formatCurrency } = useCurrency()
  // F-MM-N30: tab / staff-meal lines only when the matching switch is on.
  const houseAccounts = useHouseAccountsEnabled()
  const [openDayDialogOpen, setOpenDayDialogOpen] = useState(false)
  const [cashMovementOpen, setCashMovementOpen] = useState(false)
  const [closeWizardOpen, setCloseWizardOpen] = useState(false)
  const [shiftWizardOpen, setShiftWizardOpen] = useState(false)
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false)
  const currentQuery = useQuery({
    queryKey: ['day-ops', 'current'],
    queryFn: () => apiClient.getDayOpsCurrent(),
    refetchInterval: 30_000,
  })
  const currentDay = (currentQuery.data?.data ?? null) as BusinessDay | null

  const currentDayStatusQuery = useQuery({
    queryKey: ['currentDayStatus'],
    queryFn: () => apiClient.getCurrentDayStatus(),
    enabled: !!currentDay,
    refetchInterval: 30_000,
  })
  const dayStatus = currentDayStatusQuery.data?.data as CurrentDayStatus | undefined

  const movementsQuery = useQuery({
    queryKey: ['day-ops', 'movements', currentDay?.id],
    queryFn: () =>
      currentDay ? apiClient.listDayCashMovements(currentDay.id) : Promise.resolve(null),
    enabled: !!currentDay,
  })
  const movements = (movementsQuery.data?.data ?? []) as CashDrawerMovement[]

  // F-CTRL-N29: per-shift closings. `closings_per_day` (default 1) gates the
  // Close Shift control; when 1 the button never shows and the page behaves
  // exactly as before. shiftClosings lists the handovers recorded today.
  const closingsPerDayQuery = useQuery({
    queryKey: ['settings', 'closings_per_day'],
    queryFn: () => apiClient.getSetting('closings_per_day'),
    staleTime: 5 * 60_000,
  })
  const closingsPerDay = useMemo(() => {
    const raw = closingsPerDayQuery.data?.data
    const n = typeof raw === 'number' ? raw : Number(raw)
    return Number.isFinite(n) && n >= 1 ? Math.min(4, Math.floor(n)) : 1
  }, [closingsPerDayQuery.data])

  const shiftClosingsQuery = useQuery({
    queryKey: ['shift-closings', currentDay?.id],
    queryFn: () =>
      currentDay ? apiClient.getShiftClosings(currentDay.id) : Promise.resolve(null),
    enabled: !!currentDay && closingsPerDay > 1,
  })
  const shiftClosings = (shiftClosingsQuery.data?.data ?? []) as ShiftClosing[]

  const qc = useQueryClient()
  const undoShiftMut = useMutation({
    mutationFn: (dayId: string) => apiClient.undoLastShiftClose(dayId),
    onSuccess: (res) => {
      if (!res.success) {
        toastHelpers.error(res.message || 'Failed to undo shift close')
        return
      }
      toastHelpers.success('Last shift close undone')
      qc.invalidateQueries({ queryKey: ['shift-closings'] })
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      qc.invalidateQueries({ queryKey: ['currentDayStatus'] })
    },
    onError: (err) => toastHelpers.apiError('Undo shift close', err as Error),
  })
  const handleUndoLastShift = () => {
    if (currentDay) undoShiftMut.mutate(currentDay.id)
  }

  // Cross-day card/online settled today (swiped/received today for orders that
  // book to earlier days). The cash equivalent shows in the drawer's "Money
  // put in"; card/online never enter the drawer, so surface them here so the
  // night's terminal/bank batch (which WILL include these) isn't a surprise.
  const tenderReconQuery = useQuery({
    queryKey: ['day-ops', 'tender-recon', currentDay?.id],
    queryFn: () =>
      currentDay ? apiClient.getTenderReconciliation(currentDay.id) : Promise.resolve(null),
    enabled: !!currentDay,
  })
  const tenderRecon = tenderReconQuery.data?.success ? tenderReconQuery.data.data : null

  const hourlyQuery = useQuery({
    queryKey: ['day-ops', 'hourly', currentDay?.business_date],
    queryFn: () =>
      currentDay
        ? apiClient.getHourlySalesReport(currentDay.business_date, currentDay.business_date)
        : Promise.resolve(null),
    enabled: !!currentDay,
    refetchInterval: 60_000,
  })

  // History feeds the closing-streak badge. 30 rows is enough to render
  // the most generous streak we'd brag about; same cache key as the
  // Reports page so we don't double-fetch.
  const historyQuery = useQuery({
    queryKey: ['day-ops', 'history', { limit: 30 }],
    queryFn: () => apiClient.listDayOpsHistory({ limit: 30 }),
    staleTime: 5 * 60_000,
  })
  const closingStreak = useMemo(
    () => computeClosingStreak((historyQuery.data?.data ?? []) as BusinessDay[]),
    [historyQuery.data],
  )
  // Map the backend's series → chart buckets. Each bucket's local hour is
  // derived from its own `hour_start` ISO timestamp in the business
  // timezone (Asia/Karachi), NOT from its position in the array. The
  // previous `hour: i % 24` was the source of the F-MM-CHAAYEKHANA chart
  // drift — when the backend's series window shifted (env misconfig,
  // boundary-hour change, multi-day range), the hour labels silently
  // moved by the offset. Reading `hour_start` directly removes the
  // dependency on series ordering or length.
  const hourlyBuckets = useMemo(() => {
    const series = hourlyQuery.data?.data?.series ?? []
    return series.map((p) => ({
      hour: hourInBusinessTimezone(p.hour_start),
      gross: p.net,
      order_count: p.orders,
    }))
  }, [hourlyQuery.data])

  const cashSales = dayStatus?.cash_sales ?? 0
  const cardSales = dayStatus?.card_sales ?? 0
  const onlineSales = dayStatus?.online_sales ?? 0
  // F-MM-N30: tab payments received today. Not sales (the bill was sold and
  // filed the day it was eaten) but real money in tonight's drawer and
  // batches, so they join what the closer is expected to count — the same
  // figures the backend's StageClose expects.
  const tabCash = dayStatus?.tab_settlements_cash ?? 0
  const tabCard = dayStatus?.tab_settlements_card ?? 0
  const tabOnline = dayStatus?.tab_settlements_online ?? 0
  const tabSettledTotal = dayStatus?.tab_settlements_total ?? tabCash + tabCard + tabOnline
  const cashCollected = cashSales + tabCash
  const cardCollected = cardSales + tabCard
  const onTabSales = dayStatus?.house_account_sales ?? 0
  const staffMealOrders = dayStatus?.staff_meal_orders ?? 0
  const staffMealsCost = dayStatus?.staff_meals_cost ?? 0
  const showTabLines = houseAccounts.tabsVisible
  // With the tab lines hidden, tab cash stays inside "Cash sales" so the
  // drawer still adds up to what is expected.
  const cashSalesShown = showTabLines ? cashSales : cashCollected
  const staffMealValue = dayStatus?.staff_meal_value ?? 0
  // F-MM-N24: bills paid from a gift card balance. NOT money held tonight —
  // it arrived when the card was sold — so it is never counted and never
  // enters a variance. It is shown only when a card was actually used, and
  // only so the tiles account for the day: without it, cash + card + online
  // silently fails to reach sales and the closer hunts for the difference.
  const giftCardSales = dayStatus?.gift_card_sales ?? 0
  const hasGiftCardDay = giftCardSales > 0
  // F-MM-N23: the slice of online the marketplace pays out later. It is a
  // receivable, not money that landed tonight, so the wizard expects the
  // online figure WITHOUT it — otherwise every Foodpanda day reads "short".
  const platformSettledOnline = Math.max(0, dayStatus?.platform_settled_online ?? 0)
  const onlineExpectedBase = Math.max(0, onlineSales - platformSettledOnline)
  const onlineCollected = onlineExpectedBase + tabOnline
  // With the tab lines hidden, the card and online tiles carry their tab
  // payments, as "Cash sales" does — the close wizard expects them either way,
  // and a tile that drops them reads as money gone missing.
  const cardTile = showTabLines ? cardSales : cardCollected
  const onlineTile = showTabLines ? onlineExpectedBase : onlineCollected
  // F-CTRL-N67: name the marketplace step after the tenant's own order type
  // ("Foodpanda"). Several marketplace types are joined; none configured (a
  // day closed after the type was deleted) falls back to the generic word.
  const { raw: orderTypeRows } = useEnabledOrderTypes()
  const marketplaceLabel = useMemo(() => {
    const names = orderTypeRows.filter(isMarketplaceSettledType).map((r) => r.label.trim()).filter(Boolean)
    return names.length > 0 ? names.join(' / ') : 'Foodpanda'
  }, [orderTypeRows])
  // Slugs of the marketplace order types, in the same shape the backend's
  // paymentcfg.Slugify produces — a marketplace-settled order type is locked
  // to a provider named after itself (EnsureProvider), so this is how the
  // "Foodpanda" provider bucket is recognised in the online split.
  const marketplaceSlugs = useMemo(() => {
    const set = new Set<string>()
    orderTypeRows.filter(isMarketplaceSettledType).forEach((r) => {
      const slug = slugifyProviderLabel(r.label)
      if (slug) set.add(slug)
    })
    return set
  }, [orderTypeRows])
  // Online provider split (migration 050) — a compact reconciliation hint on
  // the Online tile, e.g. "Easypaisa · JazzCash · Meezan". Empty when the
  // feature is off / nothing attributed. The marketplace provider is left
  // OUT: that money has its own tile below, and listing it here printed the
  // same Foodpanda figure twice on the Online tile. Fallback when no order
  // type slug matches (type renamed / deleted): drop the bucket whose amount
  // equals the platform-settled figure exactly.
  const onlineSubLabel = (dayStatus?.online_submethods ?? [])
    .filter((m) => m.amount > 0)
    .filter((m) =>
      marketplaceSlugs.size > 0
        ? !marketplaceSlugs.has(m.slug)
        : !(platformSettledOnline > 0 && Math.abs(m.amount - platformSettledOnline) < 0.005),
    )
    .map((m) => `${m.label} ${formatCurrency(m.amount)}`)
    .join(' · ')
  const totalSales = dayStatus?.total_sales ?? 0
  const totalOrders = dayStatus?.total_orders ?? 0
  const refundsIssued = dayStatus?.refunds_issued ?? 0
  // Sales split (owner decision 2026-09-09): Restaurant vs marketplace. Only
  // for tenants with FEATURE_MARKETPLACE_SETTLED on (marketplaceSlugs is empty
  // otherwise — the frontend never learns a type is marketplace-settled while
  // the flag is off) AND on days that actually had marketplace orders. Every
  // other tenant / day keeps the plain four-tile row.
  const marketplaceSales = Math.max(0, dayStatus?.marketplace_sales ?? 0)
  const marketplaceOrders = Math.max(0, dayStatus?.marketplace_orders ?? 0)
  const hasMarketplaceDay = marketplaceSales > 0 && marketplaceSlugs.size > 0
  const restaurantSales = Math.max(0, totalSales - marketplaceSales)
  const restaurantOrders = Math.max(0, totalOrders - marketplaceOrders)

  const adj = useMemo(() => netCashAdjustments(movements), [movements])
  const liveExpectedCash = useMemo(
    () => computeExpectedCash({ day: currentDay, cashSales: cashCollected, movements }),
    [currentDay, cashCollected, movements],
  )
  const expectedCash =
    currentDay?.status === 'closed' && currentDay.expected_cash != null
      ? currentDay.expected_cash
      : liveExpectedCash
  const netAdjustments =
    currentDay?.status === 'closed' && currentDay.expected_cash != null
      ? currentDay.expected_cash - (currentDay.opening_cash ?? 0) - cashCollected
      : adj

  const stateInfo = dayStateLabel(currentDay?.status)
  const dayIsClosed = currentDay?.status === 'closed'

  // A shift close is a mid-day checkpoint: only while the day is open/reopened,
  // and only until (closings_per_day − 1) shift closes have been recorded (the
  // final close-out is the day close). closings_per_day=1 ⇒ never shown.
  const dayIsMutable = currentDay?.status === 'open' || currentDay?.status === 'reopened'
  const canCloseShift = !!dayIsMutable && shiftClosings.length < closingsPerDay - 1

  // When there's no active day, the only thing on the page is the start-the-day
  // CTA. Render it as a centered, visually prominent empty state instead of
  // a thin top banner with dead space below.
  if (!currentQuery.isLoading && !currentDay) {
    return (
      <>
        <StartTheDayEmptyState onStart={() => setOpenDayDialogOpen(true)} />
        <OpenDayDialog open={openDayDialogOpen} onOpenChange={setOpenDayDialogOpen} />
      </>
    )
  }

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <AgingOpenOrdersAlert variant="banner" />

      {/* F-CTRL-N35 — food fired from an offline terminal that was never
          billed. Sits at the TOP of Day Close because it hard-blocks the close
          and this screen is the only way to clear it; a manager who cannot lock
          the day must find the reason without hunting for it. Renders nothing
          when the queue is empty, which is almost always. */}
      <UnreconciledOfflineItems />

      {/* F-CTRL-N55 — orders this terminal took offline and has not synced yet.
          Distinct from the panel above: those reached the server and need a
          decision, these have not reached it at all and no backend can see them.
          A warning rather than a blocker, because the queue drains itself and a
          manager cannot clear a record that lives in another machine. */}
      <PendingOfflineSyncAlert />

      {/* F-CTRL-N72 — carts parked with "Hold". Staging the close expires them;
          a notice only, since a held cart is not a sale. */}
      <HeldCartsDayCloseNotice />

      <ClosingStreakCard
        history={(historyQuery.data?.data ?? []) as BusinessDay[]}
        streak={closingStreak}
      />

      <DayCloseHero
        state={stateInfo}
        currentDay={currentDay}
        loading={currentQuery.isLoading}
        onOpen={() => setOpenDayDialogOpen(true)}
        onClose={() => setCloseWizardOpen(true)}
        onReopen={() => setReopenDialogOpen(true)}
        canCloseShift={canCloseShift}
        nextShiftNumber={shiftClosings.length + 1}
        onShiftClose={() => setShiftWizardOpen(true)}
      />

      {currentDay && (
        <>
          {/* Two jobs, two rows on a marketplace day. Row 1 "Sales" is gross
              of refunds: Total = Restaurant + Foodpanda. Row 2 "Money you
              hold tonight" is net of refunds and POS-only: Cash + Card +
              Online, where Online has the marketplace slice taken out so it
              matches the close wizard's Online step and the Z-slip
              (F-CTRL-N67). Foodpanda's money is described once, on its sales
              tile ("paid later") — never list the same rupees under two
              labels here again. Tenants without the feature, or a day with
              no marketplace orders, get the original single four-tile row. */}
          {hasMarketplaceDay ? (
            <>
              <div className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Sales
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <BigStat
                    label="Total sales"
                    value={formatCurrency(totalSales)}
                    sub={
                      `${totalOrders} order${totalOrders === 1 ? '' : 's'}` +
                      (refundsIssued > 0 ? ` · before ${formatCurrency(refundsIssued)} refunds` : '')
                    }
                    icon={<ShoppingCart className="h-5 w-5 text-emerald-600" />}
                    loading={currentDayStatusQuery.isLoading}
                  />
                  <BigStat
                    label="Restaurant sales"
                    value={formatCurrency(restaurantSales)}
                    sub={`${restaurantOrders} order${restaurantOrders === 1 ? '' : 's'}`}
                    icon={<Store className="h-5 w-5 text-emerald-600" />}
                    loading={currentDayStatusQuery.isLoading}
                  />
                  <BigStat
                    label={`${marketplaceLabel} sales`}
                    value={formatCurrency(marketplaceSales)}
                    sub={`${marketplaceOrders} order${marketplaceOrders === 1 ? '' : 's'} · paid to you later, after commission`}
                    icon={<Globe className="h-5 w-5 text-pink-600" />}
                    loading={currentDayStatusQuery.isLoading}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Money you hold tonight
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <BigStat
                    label="Cash"
                    value={formatCurrency(cashSalesShown)}
                    icon={<Banknote className="h-5 w-5 text-emerald-600" />}
                    loading={currentDayStatusQuery.isLoading}
                  />
                  <BigStat
                    label="Card"
                    value={formatCurrency(cardTile)}
                    icon={<CreditCard className="h-5 w-5 text-blue-600" />}
                    loading={currentDayStatusQuery.isLoading}
                  />
                  <BigStat
                    label="Online"
                    value={formatCurrency(onlineTile)}
                    sub={onlineSubLabel || undefined}
                    icon={<Globe className="h-5 w-5 text-orange-600" />}
                    loading={currentDayStatusQuery.isLoading}
                  />
                </div>
                {hasGiftCardDay && (
                  <p className="text-[11px] leading-snug text-muted-foreground">
                    Plus {formatCurrency(giftCardSales)} paid by gift cards — collected when the
                    cards were sold, so there is nothing to count for it tonight.
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <BigStat
                  label="Total sales"
                  value={formatCurrency(totalSales)}
                  sub={`${totalOrders} order${totalOrders === 1 ? '' : 's'}`}
                  icon={<ShoppingCart className="h-5 w-5 text-emerald-600" />}
                  loading={currentDayStatusQuery.isLoading}
                />
                <BigStat
                  label="Cash"
                  value={formatCurrency(cashSalesShown)}
                  icon={<Banknote className="h-5 w-5 text-emerald-600" />}
                  loading={currentDayStatusQuery.isLoading}
                />
                <BigStat
                  label="Card"
                  value={formatCurrency(cardTile)}
                  icon={<CreditCard className="h-5 w-5 text-blue-600" />}
                  loading={currentDayStatusQuery.isLoading}
                />
                <BigStat
                  label="Online"
                  value={formatCurrency(onlineTile)}
                  sub={onlineSubLabel || undefined}
                  icon={<Globe className="h-5 w-5 text-orange-600" />}
                  loading={currentDayStatusQuery.isLoading}
                />
              </div>
              {hasGiftCardDay && (
                <p className="text-[11px] leading-snug text-muted-foreground">
                  Plus {formatCurrency(giftCardSales)} paid by gift cards — collected when the cards
                  were sold, so there is nothing to count for it tonight.
                </p>
              )}
            </>
          )}
          <HouseAccountDayNotes
            onTab={showTabLines ? onTabSales : 0}
            settled={
              showTabLines
                ? { cash: tabCash, card: tabCard, online: tabOnline, total: tabSettledTotal }
                : { cash: 0, card: 0, online: 0, total: 0 }
            }
            staffMeals={houseAccounts.staff ? staffMealOrders : 0}
            staffMealsCost={staffMealsCost}
            staffMealValue={staffMealValue}
            formatCurrency={formatCurrency}
          />

          <HourlySalesChart buckets={hourlyBuckets} isLoading={hourlyQuery.isLoading} />

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-lg font-semibold">Cash Drawer</CardTitle>
              <Button
                size="lg"
                variant="outline"
                className="h-11"
                onClick={() => setCashMovementOpen(true)}
                disabled={dayIsClosed}
              >
                <Plus className="mr-2 h-4 w-4" /> Add or take out money
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* The running drawer total is always visible, at every status.
                  A blind-count variant (hidden until the day was sealed) was
                  built and rejected: the operator wants the expected figure on
                  screen throughout service, and an owner who cannot see what the
                  drawer should hold has lost a number they rely on. */}
              <div className="space-y-2 text-base">
                <DrawerRow
                  label="Starting cash"
                  value={formatCurrency(currentDay.opening_cash ?? 0)}
                  help="The money that was in the drawer before the first sale today."
                />
                <DrawerRow label="+ Cash sales" value={formatCurrency(cashSalesShown)} />
                {showTabLines && tabCash > 0 && (
                  <DrawerRow
                    label="+ Tab payments (cash)"
                    value={formatCurrency(tabCash)}
                    help="Guests paying off their tab today. The bills were counted as sales on the day they were eaten."
                  />
                )}
                {netAdjustments !== 0 && (
                  <DrawerRow
                    label={netAdjustments >= 0 ? '+ Money put in' : '− Money taken out'}
                    value={formatCurrency(Math.abs(netAdjustments))}
                    className={
                      netAdjustments < 0
                        ? 'text-rose-700 dark:text-rose-300'
                        : 'text-emerald-700 dark:text-emerald-300'
                    }
                  />
                )}
                <div className="flex items-center justify-between border-t pt-2 text-lg">
                  <span className="flex items-center gap-2 font-semibold">
                    Should be in drawer
                    <HelpPopover label="What does Should be in drawer mean?">
                      <span className="font-medium">Should be in drawer</span> is what the
                      system thinks is in the drawer right now, based on the starting cash
                      and today's sales.
                    </HelpPopover>
                  </span>
                  <span className="font-bold tabular-nums">
                    {formatCurrency(expectedCash)}
                  </span>
                </div>
              </div>

              {dayIsClosed && currentDay.cash_variance != null && (
                <ClosedVarianceLine
                  variance={currentDay.cash_variance}
                  formatCurrency={formatCurrency}
                />
              )}

              {dayIsClosed && (
                <ClosedTenderVarianceLines
                  day={currentDay}
                  marketplaceLabel={marketplaceLabel}
                  formatCurrency={formatCurrency}
                />
              )}

              {movements.length > 0 && (
                <MovementsList
                  movements={movements}
                  canEdit={!dayIsClosed}
                  formatCurrency={formatCurrency}
                />
              )}
            </CardContent>
          </Card>

          {closingsPerDay > 1 && shiftClosings.length > 0 && currentDay && (
            <ShiftClosingsCard
              shifts={shiftClosings}
              businessDate={currentDay.business_date}
              canUndoLast={!!dayIsMutable}
              onUndoLast={handleUndoLastShift}
              formatCurrency={formatCurrency}
            />
          )}

          {tenderRecon && tenderRecon.items.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                  Card &amp; online for earlier days
                  <HelpPopover label="What is this?">
                    These card/online payments were taken{' '}
                    <span className="font-medium">today</span> but belong to orders
                    from <span className="font-medium">earlier business days</span>, so
                    the sale is booked to those days — not today (that's why they aren't
                    in today's Card/Online totals). The money still lands on{' '}
                    <span className="font-medium">tonight's card terminal / bank batch</span>
                    , so your batch will read higher than today's card/online sales by
                    this much. The End the Day wizard already expects it — count the batch
                    against the total it shows, not against today's sales alone.
                  </HelpPopover>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2 text-base">
                  {tenderRecon.card_cross_day > 0 && (
                    <DrawerRow
                      label="+ Card for earlier days"
                      value={formatCurrency(tenderRecon.card_cross_day)}
                      className="text-sky-700 dark:text-sky-300"
                    />
                  )}
                  {tenderRecon.online_cross_day > 0 && (
                    <DrawerRow
                      label="+ Online for earlier days"
                      value={formatCurrency(tenderRecon.online_cross_day)}
                      className="text-violet-700 dark:text-violet-300"
                    />
                  )}
                </div>

                <CrossDayTenderList
                  items={tenderRecon.items}
                  formatCurrency={formatCurrency}
                />
              </CardContent>
            </Card>
          )}
        </>
      )}

      <OpenDayDialog open={openDayDialogOpen} onOpenChange={setOpenDayDialogOpen} />
      <CashMovementDialog open={cashMovementOpen} onOpenChange={setCashMovementOpen} />
      {currentDay && (
        <CloseDayWizard
          open={closeWizardOpen}
          onOpenChange={setCloseWizardOpen}
          day={currentDay}
          cashSales={cashCollected}
          cardSales={cardCollected}
          onlineSales={onlineCollected}
          platformSettledOnline={platformSettledOnline}
          marketplaceLabel={marketplaceLabel}
          unresolvedOpenOrders={dayStatus?.unresolved_open_orders ?? 0}
          movements={movements}
          tabSettlements={showTabLines ? { cash: tabCash, card: tabCard, online: tabOnline } : undefined}
          // Suppress the review-step banner / lock Finish until both queries
          // have settled at least once. Without this, opening the wizard
          // mid-load shows `cashSales=0` (default) and the variance
          // computation falsely reports a matching drawer — the
          // ChaayéKhana 2026-05-24 incident root cause.
          isLoadingSummary={
            currentDayStatusQuery.isLoading || movementsQuery.isLoading
          }
        />
      )}
      {currentDay && closingsPerDay > 1 && (
        <ShiftCloseWizard
          open={shiftWizardOpen}
          onOpenChange={setShiftWizardOpen}
          day={currentDay}
          cashSales={cashCollected}
          cardSales={cardCollected}
          onlineSales={onlineCollected}
          platformSettledOnline={platformSettledOnline}
          priorShifts={shiftClosings}
          unresolvedOpenOrders={dayStatus?.unresolved_open_orders ?? 0}
          movements={movements}
          isLoadingSummary={
            currentDayStatusQuery.isLoading ||
            movementsQuery.isLoading ||
            shiftClosingsQuery.isLoading
          }
        />
      )}
      {currentDay && (
        <ReopenDayDialog
          open={reopenDialogOpen}
          onOpenChange={setReopenDialogOpen}
          dayId={currentDay.id}
          businessDate={currentDay.business_date}
        />
      )}
    </div>
  )
}

// F-CTRL-N29: the shift handovers recorded today. Mirrors the MovementsList /
// CrossDayTenderList row styling. The most-recent shift can be undone while the
// day is still open (the correction path for a miscount).
function ShiftClosingsCard({
  shifts,
  businessDate,
  canUndoLast,
  onUndoLast,
  formatCurrency,
}: {
  shifts: ShiftClosing[]
  businessDate: string
  canUndoLast: boolean
  onUndoLast: () => void
  formatCurrency: (n: number) => string
}) {
  const lastIndex = shifts.length - 1
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg font-semibold">
          <Users className="h-5 w-5 text-muted-foreground" /> Shifts closed today
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {shifts.map((sh, i) => {
          const balanced = Math.abs(sh.cash_variance) < 0.01
          return (
            <div key={sh.id} className="rounded-lg border bg-card px-3 py-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Shift {sh.shift_number}</span>
                    <Badge
                      variant="outline"
                      className={cn(
                        'shrink-0 text-xs font-medium',
                        balanced
                          ? 'border-emerald-300 text-emerald-700 dark:text-emerald-300'
                          : 'border-amber-300 text-amber-700 dark:text-amber-300',
                      )}
                    >
                      {balanced
                        ? 'Drawer balanced'
                        : `${formatCurrency(Math.abs(sh.cash_variance))} ${sh.cash_variance < 0 ? 'short' : 'over'}`}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {sh.closed_by_name || '—'}
                    {sh.closed_at ? ` · ${formatTimeHuman(sh.closed_at)}` : ''}
                    {sh.cash_drop > 0 ? ` · ${formatCurrency(sh.cash_drop)} to safe` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <ShiftReportButton shift={sh} businessDate={businessDate} />
                  {i === lastIndex && canUndoLast && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 text-sm text-muted-foreground hover:text-foreground"
                      onClick={onUndoLast}
                    >
                      <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Undo
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}

function DayCloseHero({
  state,
  currentDay,
  loading,
  onOpen,
  onClose,
  onReopen,
  canCloseShift,
  nextShiftNumber,
  onShiftClose,
}: {
  state: ReturnType<typeof dayStateLabel>
  currentDay: BusinessDay | null
  loading: boolean
  onOpen: () => void
  onClose: () => void
  onReopen: () => void
  canCloseShift: boolean
  nextShiftNumber: number
  onShiftClose: () => void
}) {
  if (loading) {
    return <Skeleton className="h-40 w-full rounded-2xl" />
  }

  const tone = state.tone
  const wrapper = cn(
    'rounded-2xl border-2 p-5 sm:p-6',
    tone === 'open' &&
      'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40',
    tone === 'staged' &&
      'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40',
    tone === 'closed' && 'border-muted bg-muted/40',
    tone === 'none' &&
      'border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/40',
  )

  const Icon =
    tone === 'open' || tone === 'staged'
      ? Banknote
      : tone === 'closed'
        ? Lock
        : Sunrise

  return (
    <div className={wrapper}>
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <div
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
              tone === 'open' && 'bg-emerald-500 text-white',
              tone === 'staged' && 'bg-amber-500 text-white',
              tone === 'closed' && 'bg-foreground text-background',
              tone === 'none' && 'bg-amber-400 text-white',
            )}
          >
            <Icon className="h-6 w-6" aria-hidden="true" />
          </div>
          <div className="min-w-0 space-y-1">
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{state.label}</h1>
            <p className="text-base text-foreground/70">{state.description}</p>
            {currentDay && (
              <p className="text-sm text-muted-foreground">
                {formatDateHuman(currentDay.business_date)}
                {currentDay.opened_by_name && (
                  <>
                    {' '}
                    · Started by {currentDay.opened_by_name}
                    {currentDay.opened_at ? ` at ${formatTimeHuman(currentDay.opened_at)}` : ''}
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          {!currentDay && (
            <Button
              size="lg"
              className="h-14 px-6 text-lg bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={onOpen}
            >
              <Sunrise className="mr-2 h-5 w-5" /> Start the day
            </Button>
          )}
          {currentDay && currentDay.status !== 'closed' && canCloseShift && (
            <Button
              size="lg"
              variant="outline"
              className="h-14 px-5 text-base"
              onClick={onShiftClose}
            >
              <Users className="mr-2 h-5 w-5" /> Close shift {nextShiftNumber}
            </Button>
          )}
          {currentDay && currentDay.status !== 'closed' && (
            <Button
              size="lg"
              className="h-14 px-6 text-lg bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={onClose}
            >
              <Lock className="mr-2 h-5 w-5" />
              {currentDay.status === 'staged' ? 'Finish the day' : 'End the day'}
            </Button>
          )}
          {currentDay?.status === 'closed' && (
            <div className="flex flex-wrap items-center gap-2 justify-end">
              <ZReportButton
                dayId={currentDay.id}
                enabled
                variant="default"
                size="lg"
                label="Print day summary"
              />
              <Button variant="outline" size="lg" className="h-12" onClick={onReopen}>
                <RotateCcw className="mr-2 h-4 w-4" /> Fix yesterday
              </Button>
            </div>
          )}
          {currentDay && currentDay.status === 'staged' && (
            <ZReportButton
              dayId={currentDay.id}
              enabled
              variant="outline"
              size="lg"
              label="Print day summary"
            />
          )}
        </div>
      </div>
    </div>
  )
}

function BigStat({
  label,
  value,
  sub,
  icon,
  loading,
}: {
  label: string
  value: string
  sub?: string
  icon: React.ReactNode
  loading: boolean
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border bg-card p-4">
      <div className="shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-muted-foreground truncate">{label}</div>
        {loading ? (
          <Skeleton className="mt-1 h-6 w-20" />
        ) : (
          <>
            <div className="text-lg sm:text-xl font-bold tabular-nums leading-tight">
              {value}
            </div>
            {sub && <div className="text-sm text-muted-foreground">{sub}</div>}
          </>
        )}
      </div>
    </div>
  )
}

function DrawerRow({
  label,
  value,
  className,
  help,
}: {
  label: string
  value: string
  className?: string
  help?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-foreground/80">
        {label}
        {help && <HelpPopover label={`What is ${label}?`}>{help}</HelpPopover>}
      </span>
      <span className={cn('tabular-nums font-medium', className)}>{value}</span>
    </div>
  )
}

// Cross-day card/online settled today, listed the same way as the cash drawer
// movements (bordered badge-rows, collapsed to 5 with a Show-all toggle) so the
// two panels read as one system rather than a wall of text.
function CrossDayTenderList({
  items,
  formatCurrency,
}: {
  items: CrossDayTender[]
  formatCurrency: (n: number) => string
}) {
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? items : items.slice(0, 5)
  return (
    <div className="space-y-2 border-t pt-3">
      <div className="text-sm font-semibold text-foreground">Settled today for earlier days</div>
      <ul className="space-y-1.5">
        {visible.map((it, i) => {
          const isOnline = it.method === 'online'
          return (
            <li key={`${it.order_number}-${i}`} className="rounded-lg border bg-card px-3 py-2.5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <Badge
                    variant="outline"
                    className={cn(
                      'shrink-0 text-xs font-medium capitalize',
                      isOnline
                        ? 'border-violet-300 text-violet-700 dark:text-violet-300'
                        : 'border-sky-300 text-sky-700 dark:text-sky-300',
                    )}
                  >
                    {it.method}
                  </Badge>
                  <span className="truncate text-base">
                    {it.order_number}{' '}
                    <span className="text-muted-foreground">· business day {it.sale_date}</span>
                  </span>
                </div>
                <span className="shrink-0 font-semibold tabular-nums">
                  +{formatCurrency(it.amount)}
                </span>
              </div>
            </li>
          )
        })}
      </ul>
      {items.length > 5 && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-9 text-sm"
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? (
            <>
              <ChevronUp className="mr-1 h-4 w-4" /> Show fewer
            </>
          ) : (
            <>
              <ChevronDown className="mr-1 h-4 w-4" /> Show all ({items.length})
            </>
          )}
        </Button>
      )}
    </div>
  )
}

function ClosedVarianceLine({
  variance,
  formatCurrency,
}: {
  variance: number
  formatCurrency: (n: number) => string
}) {
  const abs = Math.abs(variance)
  const isMatch = abs < 0.01
  const label = isMatch
    ? 'Drawer matched'
    : variance < 0
      ? `${formatCurrency(abs)} short`
      : `${formatCurrency(abs)} over`
  const tone = isMatch
    ? 'text-emerald-700 dark:text-emerald-300'
    : abs > 100
      ? 'text-rose-700 dark:text-rose-300'
      : 'text-amber-700 dark:text-amber-300'
  return (
    <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-3 py-2">
      <span className="text-base">Difference at close</span>
      <span className={cn('text-lg font-semibold tabular-nums', tone)}>{label}</span>
    </div>
  )
}

/**
 * Card and online reconciliation on a closed day (F-CTRL-N33).
 *
 * The backend has persisted card_variance / online_variance since F-CTRL-N17,
 * but until now no screen read them — `grep card_variance frontend/src` matched
 * nothing. So a card shortfall was captured at close and then invisible to the
 * owner forever after, while cash got a prominent "Difference at close" line.
 *
 * NULL is rendered "Not counted", never Rs 0. Only a force-close can produce a
 * NULL now (nobody attended that drawer) and a force-closed day must not read
 * like a day that reconciled to zero.
 */
function ClosedTenderVarianceLines({
  day,
  marketplaceLabel,
  formatCurrency,
}: {
  day: BusinessDay
  marketplaceLabel: string
  formatCurrency: (n: number) => string
}) {
  const rows: Array<{
    label: string
    variance?: number | null
    counted?: number | null
    phrasing?: { over: string; short: string }
  }> = [
    { label: 'Card', variance: day.card_variance, counted: day.counted_card },
    { label: 'Online', variance: day.online_variance, counted: day.counted_online },
  ]
  // F-CTRL-N67: partner-app total vs the POS, only on days that had one.
  if ((day.expected_marketplace ?? 0) > 0 || day.counted_marketplace != null) {
    rows.push({
      label: `${marketplaceLabel} app`,
      variance: day.marketplace_variance,
      counted: day.counted_marketplace,
      phrasing: { over: 'more in app', short: 'less in app' },
    })
  }
  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const notCounted = r.variance == null && r.counted == null
        const v = r.variance ?? 0
        const abs = Math.abs(v)
        const isMatch = !notCounted && abs < 0.01
        return (
          <div
            key={r.label}
            className="flex items-center justify-between rounded-lg border bg-muted/30 px-3 py-2"
          >
            <span className="text-base">{r.label} at close</span>
            <span
              className={cn(
                'text-lg font-semibold tabular-nums',
                notCounted && 'text-muted-foreground',
                isMatch && 'text-emerald-700 dark:text-emerald-300',
                !notCounted && !isMatch && abs > 100 && 'text-rose-700 dark:text-rose-300',
                !notCounted && !isMatch && abs <= 100 && 'text-amber-700 dark:text-amber-300',
              )}
            >
              {notCounted
                ? 'Not counted'
                : isMatch
                  ? 'Matched'
                  : `${formatCurrency(abs)} ${v < 0 ? (r.phrasing?.short ?? 'short') : (r.phrasing?.over ?? 'over')}`}
            </span>
          </div>
        )
      })}
    </div>
  )
}

function MovementsList({
  movements,
  canEdit,
  formatCurrency,
}: {
  movements: CashDrawerMovement[]
  canEdit: boolean
  formatCurrency: (n: number) => string
}) {
  const qc = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editAmount, setEditAmount] = useState('')
  const [editReason, setEditReason] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<CashDrawerMovement | null>(null)
  // F-CTRL-N75: a stock cash-out whose items are being added.
  const [addingItems, setAddingItems] = useState<CashDrawerMovement | null>(null)
  const [showAll, setShowAll] = useState(false)
  const visibleMovements = showAll ? movements : movements.slice(0, 5)

  const updateMut = useMutation({
    mutationFn: ({ id, amount, reason }: { id: string; amount: number; reason: string }) =>
      apiClient.updateCashMovement(id, { amount, reason }),
    onSuccess: () => {
      toastHelpers.success('Saved')
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      setEditingId(null)
    },
    onError: (err: Error) => toastHelpers.apiError('Save changes', err),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiClient.deleteCashMovement(id),
    onSuccess: () => {
      toastHelpers.success('Removed')
      qc.invalidateQueries({ queryKey: ['day-ops'] })
      setDeleteTarget(null)
    },
    onError: (err: Error) => toastHelpers.apiError('Remove entry', err),
  })

  const startEdit = (m: CashDrawerMovement) => {
    setEditingId(m.id)
    setEditAmount(String(m.amount))
    setEditReason(m.reason)
  }

  return (
    <>
      <div className="space-y-2 pt-1">
        <div className="text-sm font-semibold text-foreground">Money put in &amp; taken out today</div>
        <ul className="space-y-1.5">
          {visibleMovements.map((m) => {
            const isIn = m.movement_type === 'paid_in'
            return (
              <li
                key={m.id}
                className="rounded-lg border bg-card px-3 py-2.5"
              >
                {editingId === m.id ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      type="text"
                      inputMode="decimal"
                      // F-CTRL-N75: the amount of cash that paid for stock on
                      // the shelf is what the purchase cost.
                      disabled={!!m.purchase_order_id}
                      title={m.purchase_order_id ? BT.lockedHint : undefined}
                      value={editAmount}
                      onChange={(e) =>
                        setEditAmount(e.target.value.replace(/[^0-9.]/g, ''))
                      }
                      className="h-10 w-28 tabular-nums"
                    />
                    <Input
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                      className="h-10 flex-1 min-w-[8rem]"
                    />
                    <Button
                      size="sm"
                      onClick={() => {
                        const amt = Number(editAmount)
                        if (amt > 0 && editReason.trim()) {
                          updateMut.mutate({
                            id: m.id,
                            amount: amt,
                            reason: editReason.trim(),
                          })
                        }
                      }}
                      disabled={updateMut.isPending}
                    >
                      Save
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          'shrink-0 text-xs font-medium',
                          isIn
                            ? 'border-emerald-300 text-emerald-700 dark:text-emerald-300'
                            : 'border-rose-300 text-rose-700 dark:text-rose-300',
                        )}
                      >
                        {isIn ? 'Money put in' : 'Money taken out'}
                      </Badge>
                      <span className="truncate text-base">{m.reason}</span>
                      {m.purchase_order_id && (
                        <Badge variant="outline" className="shrink-0 text-xs font-medium">
                          {BT.stockAdded}
                        </Badge>
                      )}
                      {m.needs_items && (
                        <Badge
                          variant="outline"
                          className="shrink-0 border-amber-300 text-xs font-medium text-amber-800 dark:text-amber-300"
                        >
                          {BT.stockNotAdded}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span
                        className={cn(
                          'font-semibold tabular-nums',
                          isIn
                            ? 'text-emerald-700 dark:text-emerald-300'
                            : 'text-rose-700 dark:text-rose-300',
                        )}
                      >
                        {isIn ? '+' : '−'}
                        {formatCurrency(m.amount)}
                      </span>
                      {m.needs_items && (
                        <Button size="sm" variant="outline" className="h-8" onClick={() => setAddingItems(m)}>
                          {BT.addItems}
                        </Button>
                      )}
                      {canEdit && (
                        <>
                          <button
                            type="button"
                            onClick={() => startEdit(m)}
                            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          {!m.purchase_order_id && (
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(m)}
                              className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive"
                              aria-label="Remove"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
        {movements.length > 5 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 text-sm"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? (
              <>
                <ChevronUp className="mr-1 h-4 w-4" /> Show fewer
              </>
            ) : (
              <>
                <ChevronDown className="mr-1 h-4 w-4" /> Show all ({movements.length})
              </>
            )}
          </Button>
        )}
      </div>

      <StockCashOutItemsDialog
        movement={addingItems}
        onClose={() => setAddingItems(null)}
        notify={(kind, msg) => (kind === 'success' ? toastHelpers.success(msg) : toastHelpers.error(msg))}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Remove this entry?</DialogTitle>
            <DialogDescription>
              {deleteTarget && (
                <>
                  Remove the{' '}
                  <span className="font-medium">
                    {deleteTarget.movement_type === 'paid_in'
                      ? 'money put in'
                      : 'money taken out'}
                  </span>{' '}
                  of {formatCurrency(deleteTarget.amount)} for &ldquo;{deleteTarget.reason}
                  &rdquo;? This can't be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending ? 'Removing…' : 'Yes, remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * F-MM-N30 — the house-account lines that explain the tiles. Renders nothing
 * on a day without tabs or staff meals.
 */
function HouseAccountDayNotes({
  onTab,
  settled,
  staffMeals,
  staffMealValue,
  staffMealsCost,
  formatCurrency,
}: {
  onTab: number
  settled: { cash: number; card: number; online: number; total: number }
  staffMeals: number
  staffMealValue: number
  staffMealsCost: number
  formatCurrency: (n: number) => string
}) {
  if (onTab <= 0 && settled.total <= 0 && staffMeals <= 0) return null
  const parts = (
    [
      ['cash', settled.cash],
      ['card', settled.card],
      ['online', settled.online],
    ] as const
  )
    .filter(([, v]) => v > 0)
    .map(([k, v]) => `${formatCurrency(v)} ${k}`)
  return (
    <div className="space-y-1 text-[11px] leading-snug text-muted-foreground">
      {onTab > 0 && (
        <p>
          {formatCurrency(onTab)} of today&apos;s sales went on customer tabs — sold and filed today, collected when
          the guest pays, so there is nothing to count for it tonight.
        </p>
      )}
      {settled.total > 0 && (
        <p>
          Tab payments received today: {formatCurrency(settled.total)} ({parts.join(', ')}). Included in what you
          count below.
        </p>
      )}
      {staffMeals > 0 && (
        <p>
          Staff meals: {staffMeals} meal{staffMeals === 1 ? '' : 's'} · {formatCurrency(staffMealValue)} menu value ·{' '}
          {formatCurrency(staffMealsCost)} cost (on the monthly P&amp;L). Not sales and nothing to collect.
        </p>
      )}
    </div>
  )
}
